/**
 * Captura de foto para o fluxo de vistoria (TV-2 — captura via Mobi.Core).
 *
 * O caminho legado fotografava o STREAM da chamada (limitado a 960x720 e degradado
 * adaptativamente — a qualidade ruim das capturas vinha daí). Aqui a foto sai do SENSOR:
 * ImageCapture.takePhoto() devolve a resolução nativa da câmera, sem passar pelo encoder
 * do WebRTC e sem perturbar a chamada. iOS Safari não tem a API → fallback no frame do
 * stream (qualidade de hoje), sempre exportado como JPEG (o PNG legado só pesava 5-10x
 * mais sem ganhar nada — é lossless de uma fonte que já nasceu limitada).
 */

const TAKE_PHOTO_TIMEOUT_MS = 4000;
const JPEG_QUALITY = 0.92;

declare global {
  interface Window {
    ImageCapture?: {
      new (track: MediaStreamTrack): {
        takePhoto(settings?: object): Promise<Blob>;
        getPhotoCapabilities(): Promise<{ imageWidth?: { max: number }; imageHeight?: { max: number } }>;
      };
    };
  }
}

/** iOS/iPadOS (todos os browsers são WebKit lá). iPadOS 13+ se reporta como "Macintosh". */
function isIOSWebKit(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const iOSDevice = /iP(hone|ad|od)/.test(ua);
  const iPadOSAsMac = /Macintosh/.test(ua) && typeof document !== "undefined" && "ontouchend" in document;
  return iOSDevice || iPadOSAsMac;
}

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error("takePhoto timeout")), ms);
    promise.then(
      (v) => {
        clearTimeout(timer);
        resolve(v);
      },
      (e) => {
        clearTimeout(timer);
        reject(e);
      }
    );
  });
}

/** Foto na resolução nativa do sensor (Android/Chrome). null = API indisponível/falhou. */
export async function takeNativePhoto(track: MediaStreamTrack | undefined): Promise<Blob | null> {
  // iOS/WebKit: o ImageCapture é parcial/bugado e o takePhoto reconfigura a câmera, matando o
  // track ao vivo da chamada (tela preta que não volta). NUNCA instanciar no iOS — cai no frame
  // do stream. Gate por plataforma além do !window.ImageCapture (à prova de WebKit que passe a expor).
  if (!track || track.readyState !== "live" || isIOSWebKit() || !window.ImageCapture) return null;

  try {
    const imageCapture = new window.ImageCapture(track);

    // Pede o teto do sensor quando o device expõe as capacidades (alguns Androids tiram
    // na resolução do stream se não pedir explicitamente).
    let settings: object | undefined;
    try {
      const caps = await imageCapture.getPhotoCapabilities();
      if (caps.imageWidth?.max && caps.imageHeight?.max) {
        settings = { imageWidth: caps.imageWidth.max, imageHeight: caps.imageHeight.max };
      }
    } catch {
      settings = undefined;
    }

    const blob = await withTimeout(imageCapture.takePhoto(settings), TAKE_PHOTO_TIMEOUT_MS);
    console.info("[capturePhoto] takePhoto ok", { size: blob.size, type: blob.type });
    return blob;
  } catch (error) {
    console.warn("[capturePhoto] takePhoto falhou — fallback no frame do stream", error);
    return null;
  }
}

/** Fallback: frame atual do vídeo → JPEG (qualidade limitada pelo stream — iOS). */
export async function captureFrameAsJpeg(video: HTMLVideoElement): Promise<Blob | null> {
  if (!video || video.videoWidth === 0) return null;

  const canvas = document.createElement("canvas");
  canvas.width = video.videoWidth;
  canvas.height = video.videoHeight;
  canvas.getContext("2d")!.drawImage(video, 0, 0);

  return new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", JPEG_QUALITY));
}

/** Estratégia completa: sensor primeiro, frame do stream como fallback. */
export async function capturePhotoBlob(
  track: MediaStreamTrack | undefined,
  video: HTMLVideoElement
): Promise<Blob | null> {
  const ios = isIOSWebKit();
  const trackAntes = track?.readyState;
  const blob = (await takeNativePhoto(track)) ?? (await captureFrameAsJpeg(video));
  // Diagnóstico do bug iOS (tela preta): se trackDepois !== "live", a captura matou a câmera —
  // e hasImageCapture diz se o culpado foi o ImageCapture (Opção 1 resolve) ou outra coisa.
  console.info("[capturePhoto] resultado", {
    ios,
    hasImageCapture: !!window.ImageCapture,
    trackAntes,
    trackDepois: track?.readyState,
    size: blob?.size ?? 0,
  });
  return blob;
}
